/**
 * One builder for every "hand this object to the agent" link in the app.
 *
 * There were 18 hand-rolled `?prefill=` call sites before this, each assembling
 * the same query string slightly differently. This consolidates them; it does
 * not invent a convention. The param vocabulary here is the subset of
 * `useResolveCreateParams` that row-level handoffs actually need — that hook is
 * the source of truth and validates every value it reads.
 *
 * Two constraints inherited from the resolver, both load-bearing:
 *
 *  1. **`sourceType`/`sourceId` are NOT ours to use.** They belong to the
 *     Discover pre-prompt system: `useCreatePageUrlParams.ts:428` builds a
 *     `prePromptKey` the moment both are present, `:436` enables the query, and
 *     `:451` fetches `/api/discover/pre-prompt`. That endpoint validates against
 *     its own union — `news | social_post | trending_topic | reddit`
 *     (`server/api/discover/pre-prompt.get.ts:89-190`) — and 400s on anything
 *     else, while its error path (`:487-499`) is gated on `articleIdParam`,
 *     which a row link never sets. Emitting them would fire a doomed request
 *     whose failure is swallowed. Provenance therefore rides in the prompt text
 *     itself, which is also what the competitor's row links do — theirs carry
 *     the prompt and nothing else.
 *
 *  2. `autosubmit=1` sends the turn immediately. Opt-in per call site, never a
 *     default: some handoffs must NOT autosubmit — see PlanActionsMenu, where an
 *     autosubmitted send bypasses the /strategy tool. Row links pre-fill and
 *     wait so a stray click never spends tokens or acts on an account without
 *     the user reading the prompt first.
 *
 *     ⚠️ `autosubmit` has NO producer in the shipped app at all. Its only
 *     emitter was `HomeComposer.tsx:30`, reachable solely from
 *     `IndexRedesignV2.tsx`, which no route imported; both were deleted on
 *     2026-08-26 in the composer census. The consumer side is live and tested
 *     (`useCreatePageUrlParams.ts:599`), so this is a wired-but-unused option,
 *     not a broken one — the first call site to pass it will be its first.
 */

import { logger } from '@/utils/logger';

export type AgentMode = 'chat' | 'plan' | 'video' | 'image';

export type AgentLinkSource =
  | 'post'
  | 'project'
  | 'product'
  | 'look'
  | 'avatar'
  | 'article'
  | 'board-card'
  | 'insight'
  | 'connection'
  | 'workflow-run'
  | 'lens';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface AgentLinkOptions {
  /** The message the composer opens with. Sent verbatim. */
  prompt: string;
  /** Composer mode. Omit for the default chat surface. */
  mode?: AgentMode;
  /** Coarse origin. Already used by the board and discover handoffs. */
  intent?: string;
  /** The object this link came from. `id` must be a UUID or it is omitted. */
  source?: { type: AgentLinkSource; id: string };
  /** Platform filter, as carried by the discover handoff. */
  platforms?: string[];
  /** Force a brand-new thread instead of continuing the current one. */
  newThread?: boolean;
  /**
   * Send immediately on arrival. Off by default — see note 2 above. Only set
   * this where the prompt is unambiguous and acting on it costs nothing.
   */
  autosubmit?: boolean;
}

/**
 * Kept well under the ~2000-char practical URL ceiling so a long prompt can
 * never truncate mid-sentence in a browser, proxy, or email client.
 */
export const MAX_PROMPT_CHARS = 1200;

/**
 * Returns short prompts BYTE-FOR-BYTE, including trailing whitespace. A slash
 * prefill such as `'/brand-kit '` depends on its trailing space to register as a
 * completed command — see BrandScanDialog.tsx:77-79. An earlier version of this
 * function called `.trim()` unconditionally and silently broke that.
 */
export function truncatePrompt(prompt: string, max = MAX_PROMPT_CHARS): string {
  if (prompt.length <= max) return prompt;
  const cut = prompt.slice(0, max);
  const lastBreak = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('\n'), cut.lastIndexOf(' '));
  return (lastBreak > max * 0.6 ? cut.slice(0, lastBreak) : cut).trimEnd();
}

export function buildAgentUrl(options: AgentLinkOptions): string {
  const { prompt, mode, intent, source, platforms, newThread, autosubmit } = options;

  const params = new URLSearchParams();
  if (mode) params.set('mode', mode);
  if (intent) params.set('intent', intent);

  if (source && import.meta.env.DEV && !UUID_RE.test(source.id)) {
    logger.warn(
      `[buildAgentUrl] non-UUID id for ${source.type}: ${source.id}. Not fatal — ` +
        `provenance rides in the prompt text, not in query params — but check the ` +
        `caller is passing the row's real id.`,
    );
  }

  if (platforms?.length) params.set('platforms', platforms.join(','));
  if (newThread) params.set('new', 'true');
  if (autosubmit) params.set('autosubmit', '1');

  params.set('prefill', truncatePrompt(prompt));

  return `/?${params.toString()}`;
}
